"use client"

import { useEffect, useSyncExternalStore } from "react"
import { ArrowRight, Package } from "lucide-react"

import { contentAnalytics } from "@/lib/analytics"
import { withAmazonTagForPlacement } from "@/lib/affiliate"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import type { ToolResultPickData } from "@/lib/tool-result-pick"
import { AffiliateDisclosure } from "./affiliate-disclosure"

/**
 * The ASIN each tool's in-result pick is showing right now, keyed by tool slug.
 * A below-results block on the same route (ToolPicksBlock) reads it and drops
 * that row, so one product never renders twice on a page.
 */
const activeAsins = new Map<string, string>()
const listeners = new Set<() => void>()

function setActiveAsin(toolSlug: string, asin: string | null) {
  const prev = activeAsins.get(toolSlug) ?? null
  if (prev === asin) return
  if (asin) activeAsins.set(toolSlug, asin)
  else activeAsins.delete(toolSlug)
  listeners.forEach((l) => l())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** ASIN of the in-result pick currently rendered for `toolSlug`, else null. */
export function useActiveResultAsin(toolSlug: string): string | null {
  return useSyncExternalStore(
    subscribe,
    () => activeAsins.get(toolSlug) ?? null,
    () => null,
  )
}

/**
 * One product row inside a calculator result: the amount the result computed
 * ("You need 2 × 7 lb bags"), then the product and its Amazon link. Build the
 * data with `buildToolResultPick` (lib/tool-result-pick.ts); it returns null
 * for a demoted ASIN, and so does this component.
 */
export function ToolResultPick({
  pick,
  className,
}: {
  pick: ToolResultPickData | null
  className?: string
}) {
  const toolSlug = pick?.toolSlug ?? ""
  const asin = pick?.asin ?? null
  const href = pick ? withAmazonTagForPlacement(pick.affiliateUrl, pick.surface) : undefined

  useEffect(() => {
    if (!toolSlug) return
    setActiveAsin(toolSlug, asin)
    return () => setActiveAsin(toolSlug, null)
  }, [toolSlug, asin])

  const impressionRef = useAffiliateImpression(pick?.surface, {
    article_slug: `tool:${toolSlug}`,
    product_name: pick?.name,
    affiliate_link: href,
    asin: asin ?? undefined,
    position: pick?.position,
    pick_index: pick ? pick.position - 1 : undefined,
    has_quantity: Boolean(pick?.quantityLine),
  })

  if (!pick) return null

  return (
    <div ref={impressionRef} className={className ?? "mt-5 rounded-2xl border border-gray-200 bg-gray-50 p-3.5"}>
      {pick.quantityLine && (
        <p className="mb-2.5 text-sm font-bold text-gray-900">{pick.quantityLine}</p>
      )}
      <div className="flex items-center gap-3">
        <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-lg border border-gray-100 bg-white">
          {pick.imageUrl ? (
            // Plain img: Amazon CDN thumbnail, same as ToolPicksBlock.
            <img src={pick.imageUrl} alt="" loading="lazy" className="h-full w-full object-contain p-1" />
          ) : (
            <Package className="h-5 w-5 text-gray-400" aria-hidden="true" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <span className="block text-sm font-semibold leading-snug text-gray-900">{pick.name}</span>
          <span className="mt-0.5 block text-[12.5px] text-gray-500">{pick.reason}</span>
        </div>
      </div>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow sponsored"
        data-affiliate="true"
        data-affiliate-placement={pick.surface}
        data-affiliate-position={pick.position}
        onClick={() =>
          contentAnalytics.affiliateProductClicked(pick.surface, {
            productName: pick.name,
            affiliateLink: href ?? pick.affiliateUrl,
            asin: pick.asin,
            article_slug: `tool:${pick.toolSlug}`,
            position: pick.position,
            pick_index: pick.position - 1,
            has_quantity: Boolean(pick.quantityLine),
          })
        }
        className="mt-3 flex min-h-11 w-full items-center justify-center gap-1 rounded-full bg-action px-4 text-[13px] font-bold text-white no-underline hover:bg-action-hover"
      >
        Check price on Amazon
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </a>
      <AffiliateDisclosure className="mt-2 text-center" />
    </div>
  )
}
